/**
 * Координация удаления элементов через несколько слоёв.
 * Это не store, а функции-экшены, которые вызывают методы разных store.
 */
import { SelectionKind } from "@/shared/store/editorTypes";
import { useWallsStore } from "../layers/wallsStore";
import { useFixturesStore } from "../layers/fixturesStore";
import { useFurnitureFixturesStore, usePlumbingStore } from "../layers/plumbingStore";
import { useWallFinishStore } from "../layers/wallFinishStore";
import { useWiringStore } from "../layers/wiringStore";
import { usePipesStore } from "../layers/pipesStore";
import { useCommentsStore } from "../layers/commentsStore";
import { useFloorsStore } from "../layers/floorsStore";
import { floorItemOwner } from "../layers/floorItemsStore";
import type { CommentTarget } from "@/shared/plan";

export type RemoveTarget = {
	kind: SelectionKind;
	id: string;
} | null;

/**
 * Удалить стену и все зависимые элементы.
 * - Приборы на этой стене
 * - Кабели от приборов на этой стене
 * - Трубы от точек водоснабжения на этой стене
 */
export const removeWall = (wallId: string) => {
	const wallsState = useWallsStore.getState();
	if (!wallsState.items.walls[wallId]) return;

	const fixturesState = useFixturesStore.getState();
	const plumbingState = usePlumbingStore.getState();
	const wiringState = useWiringStore.getState();
	const pipesState = usePipesStore.getState();

	// Найти приборы (электрика) на этой стене
	const electricalFixturesToRemove = new Set(
		Object.entries(fixturesState.items)
			.filter(([, f]) => f.wallId === wallId)
			.map(([id]) => id),
	);

	// Найти приборы (сантехника) на этой стене
	const plumbingFixturesToRemove = new Set(
		Object.entries(plumbingState.items)
			.filter(([, f]) => f.wallId === wallId)
			.map(([id]) => id),
	);

	// Мебель и декор на этой стене — картины, экран, телевизор
	const furnitureState = useFurnitureFixturesStore.getState();
	const remainingFurnitureFixtures = Object.fromEntries(
		Object.entries(furnitureState.items).filter(([, f]) => f.wallId !== wallId),
	);

	// Объединяем все удаляемые приборы
	const allFixturesToRemove = new Set([...electricalFixturesToRemove, ...plumbingFixturesToRemove]);

	// Удалить приборы
	const remainingElectricalFixtures = Object.fromEntries(
		Object.entries(fixturesState.items).filter(([id]) => !electricalFixturesToRemove.has(id)),
	);

	const remainingPlumbingFixtures = Object.fromEntries(
		Object.entries(plumbingState.items).filter(([id]) => !plumbingFixturesToRemove.has(id)),
	);

	// Удалить кабели от этих приборов
	const remainingCables = Object.fromEntries(
		Object.entries(wiringState.items).filter(
			([, c]) => !allFixturesToRemove.has(c.from) && !allFixturesToRemove.has(c.to),
		),
	);
	const remainingConnections = Object.fromEntries(
		Object.entries(wiringState.connections).filter(
			([, connection]) =>
				!allFixturesToRemove.has(connection.from) && !allFixturesToRemove.has(connection.to),
		),
	);

	// Удалить трубы от точек водоснабжения на этой стене
	const remainingPipes = Object.fromEntries(
		Object.entries(pipesState.items).filter(
			([, p]) => !plumbingFixturesToRemove.has(p.from) && !plumbingFixturesToRemove.has(p.to),
		),
	);

	useFixturesStore.getState().restoreFixtures(remainingElectricalFixtures);
	usePlumbingStore.getState().restoreFixtures(remainingPlumbingFixtures);
	useFurnitureFixturesStore.getState().restoreFixtures(remainingFurnitureFixtures);
	useWiringStore.getState().restoreCables(remainingCables);
	useWiringStore.getState().restoreLightingConnections(remainingConnections);
	// Отделка обеих сторон уходит вместе со стеной
	const finishState = useWallFinishStore.getState();
	finishState.removeFinish([
		{ wallId, side: 1 },
		{ wallId, side: -1 },
	]);
	usePipesStore.getState().restorePipes(remainingPipes);

	// Удалить саму стену
	const graph = wallsState.items;
	const walls = { ...graph.walls };
	const openings = { ...graph.openings };
	const nodes = { ...graph.nodes };

	delete walls[wallId];
	for (const o of Object.values(openings)) {
		if (o.wallId === wallId) delete openings[o.id];
	}

	// Узлы без единой стены больше не нужны
	const used = new Set<string>();
	for (const w of Object.values(walls)) {
		used.add(w.a);
		used.add(w.b);
	}
	for (const id of Object.keys(nodes)) {
		if (!used.has(id)) delete nodes[id];
	}

	useWallsStore.getState().restoreWalls({ nodes, walls, openings });
};

/**
 * Удалить узел (это удалит все стены, подходящие к этому узлу).
 */
export const removeNode = (nodeId: string) => {
	const wallsState = useWallsStore.getState();
	const graph = wallsState.items;
	const node = graph.nodes[nodeId];
	if (!node) return;

	// Найти все стены, подходящие к этому узлу
	const wallsToRemove = new Set<string>();
	for (const [wid, w] of Object.entries(graph.walls)) {
		if (w.a === nodeId || w.b === nodeId) {
			wallsToRemove.add(wid);
		}
	}

	// Удалить каждую стену
	for (const wallId of wallsToRemove) {
		removeWall(wallId);
	}
};

/**
 * Удалить проём.
 */
export const removeOpening = (openingId: string) => {
	const wallsState = useWallsStore.getState();
	const graph = wallsState.items;
	const openings = { ...graph.openings };
	delete openings[openingId];
	useWallsStore.getState().restoreWalls({ ...graph, openings });
};

/**
 * Удалить прибор и его кабели; точку водоснабжения — с её трубами.
 */
export const removeFixture = (fixtureId: string) => {
	const fixturesState = useFixturesStore.getState();
	const plumbingState = usePlumbingStore.getState();
	const wiringState = useWiringStore.getState();

	const isElectrical = fixtureId in fixturesState.items;
	const isPlumbing = fixtureId in plumbingState.items;

	// Картина или телевизор на стене: без кабелей и труб — просто убираем
	const furnitureState = useFurnitureFixturesStore.getState();
	if (fixtureId in furnitureState.items) {
		const fixtures = { ...furnitureState.items };
		delete fixtures[fixtureId];
		furnitureState.restoreFixtures(fixtures);
		return;
	}

	if (!isElectrical && !isPlumbing) return;

	// Удалить кабели, подходящие к этому прибору
	const remainingCables = Object.fromEntries(
		Object.entries(wiringState.items).filter(([, c]) => c.from !== fixtureId && c.to !== fixtureId),
	);
	const remainingConnections = Object.fromEntries(
		Object.entries(wiringState.connections).filter(
			([, connection]) => connection.from !== fixtureId && connection.to !== fixtureId,
		),
	);

	useWiringStore.getState().restoreCables(remainingCables);
	useWiringStore.getState().restoreLightingConnections(remainingConnections);

	// Удалить сам прибор
	if (isElectrical) {
		const fixtures = { ...fixturesState.items };
		delete fixtures[fixtureId];
		useFixturesStore.getState().restoreFixtures(fixtures);
	} else if (isPlumbing) {
		// Трубы, подходящие к этой точке, уходят вместе с ней
		const pipesState = usePipesStore.getState();
		const remainingPipes = Object.fromEntries(
			Object.entries(pipesState.items).filter(
				([, p]) => p.from !== fixtureId && p.to !== fixtureId,
			),
		);
		usePipesStore.getState().restorePipes(remainingPipes);

		const fixtures = { ...plumbingState.items };
		delete fixtures[fixtureId];
		usePlumbingStore.getState().restoreFixtures(fixtures);
	}
};

/**
 * Удалить светильник на потолке и его кабели.
 */
export const removeCeilingItem = (itemId: string) => {
	const fixturesState = useFixturesStore.getState();
	if (!fixturesState.ceilingItems[itemId]) return;

	const wiringState = useWiringStore.getState();
	const remainingCables = Object.fromEntries(
		Object.entries(wiringState.items).filter(([, c]) => c.from !== itemId && c.to !== itemId),
	);
	const remainingConnections = Object.fromEntries(
		Object.entries(wiringState.connections).filter(
			([, connection]) => connection.from !== itemId && connection.to !== itemId,
		),
	);
	useWiringStore.getState().restoreCables(remainingCables);
	useWiringStore.getState().restoreLightingConnections(remainingConnections);

	const items = { ...fixturesState.ceilingItems };
	delete items[itemId];
	useFixturesStore.getState().restoreCeilingItems(items);
};

/**
 * Удалить предмет на полу.
 */
export const removeFloorItem = (itemId: string) => {
	const owner = floorItemOwner(itemId);
	if (!owner) return;
	const items = { ...owner.getState().items };
	delete items[itemId];
	owner.getState().restoreFloorItems(items);
};

/**
 * Удалить комнату (пол).
 */
export const removeRoom = (roomId: string) => {
	const floorsState = useFloorsStore.getState();
	floorsState.removeRoomFloor(roomId);
};

/**
 * Удалить кабель.
 */
export const removeCable = (cableId: string) => {
	const wiringState = useWiringStore.getState();
	if (!wiringState.items[cableId]) return;
	const cables = { ...wiringState.items };
	delete cables[cableId];
	useWiringStore.getState().restoreCables(cables);
};

/** Удалить соединительную линию светильника. */
export const removeLightingConnection = (connectionId: string) => {
	const wiringState = useWiringStore.getState();
	if (!wiringState.connections[connectionId]) return;
	const connections = { ...wiringState.connections };
	delete connections[connectionId];
	wiringState.restoreLightingConnections(connections);
};

/**
 * Удалить трубу.
 */
export const removePipe = (pipeId: string) => {
	const pipesState = usePipesStore.getState();
	if (!pipesState.items[pipeId]) return;
	const pipes = { ...pipesState.items };
	delete pipes[pipeId];
	usePipesStore.getState().restorePipes(pipes);
};

/** Есть ли ещё объект, к которому привязан комментарий. */
const commentTargetExists = (target: CommentTarget): boolean => {
	const graph = useWallsStore.getState().items;
	switch (target.kind) {
		case "item":
			return floorItemOwner(target.id) !== null;
		case "light":
			return target.id in useFixturesStore.getState().ceilingItems;
		case "fixture":
			return (
				target.id in useFixturesStore.getState().items ||
				target.id in usePlumbingStore.getState().items ||
				target.id in useFurnitureFixturesStore.getState().items
			);
		case "opening":
			return target.id in graph.openings;
		case "wall":
			return target.id in graph.walls;
	}
};

/**
 * Комментарии, привязанные к объектам, — для удаления вместе с ними: те, чей объект
 * сейчас есть. Потерявшие объект раньше (стену разрезали) этим удалением не трогаем.
 */
const attachedComments = (): string[] =>
	Object.values(useCommentsStore.getState().items)
		.filter((comment) => comment.target && commentTargetExists(comment.target))
		.map((comment) => comment.id);

/**
 * Основной диспетчер удаления. Пропал объект — пропали и комментарии к нему,
 * как кабели с прибором: и к нему самому, и к тому, что ушло вместе с ним (приборы
 * и проёмы удалённой стены).
 */
export const remove = (target: RemoveTarget) => {
	if (!target) return;
	const attached = attachedComments();
	removeObject(target);
	const comments = useCommentsStore.getState();
	for (const id of attached) {
		const comment = comments.items[id];
		if (comment?.target && !commentTargetExists(comment.target)) comments.removeComment(id);
	}
};

const removeObject = ({ kind, id }: NonNullable<RemoveTarget>) => {
	switch (kind) {
		case SelectionKind.Wall:
			removeWall(id);
			break;
		case SelectionKind.Node:
			removeNode(id);
			break;
		case SelectionKind.Opening:
			removeOpening(id);
			break;
		case SelectionKind.Fixture:
			removeFixture(id);
			break;
		case SelectionKind.Item:
			removeFloorItem(id);
			break;
		case SelectionKind.Room:
			removeRoom(id);
			break;
		case SelectionKind.Cable:
			removeCable(id);
			break;
		case SelectionKind.LightingConnection:
			removeLightingConnection(id);
			break;
		case SelectionKind.Light:
			removeCeilingItem(id);
			break;
		case SelectionKind.Pipe:
			removePipe(id);
			break;
		case SelectionKind.Comment:
			useCommentsStore.getState().removeComment(id);
			break;
	}
};
